/**
 * BaseCollector - Abstract base class for all collectors
 * 
 * Provides common functionality:
 * - Event emission via EventBus
 * - Lifecycle management (initialize, destroy)
 * - Base metadata generation
 */

import type { EventBus } from '../core/EventBus.js';
import type { EventMetadata } from '../models/BaseEvent.js';
import { generateBaseMetadata } from '../models/BaseEvent.js';

export abstract class BaseCollector {
  protected name: string;
  protected eventBus: EventBus;
  protected sessionId: string;
  protected userId: string | null;
  protected debug: boolean;
  protected isInitialized: boolean = false;

  constructor(
    name: string,
    eventBus: EventBus,
    sessionId: string,
    userId: string | null,
    debug: boolean = false
  ) {
    this.name = name;
    this.eventBus = eventBus;
    this.sessionId = sessionId;
    this.userId = userId;
    this.debug = debug;
  }

  /**
   * Initialize the collector
   * Must be implemented by subclasses
   */
  abstract initialize(): void;

  /**
   * Destroy the collector and cleanup
   * Must be implemented by subclasses
   */
  abstract destroy(): void;

  /**
   * Emit an event through the EventBus
   */
  protected emit<T = any>(eventType: string, data: T): void {
    this.eventBus.emitSync(eventType, data);
    this.log(`Emitted event: ${eventType}`);
  }

  /**
   * Get base metadata for events
   */
  protected getBaseMetadata(): EventMetadata {
    return generateBaseMetadata(this.sessionId, this.userId);
  }

  /**
   * Update session/user context
   */
  updateContext(sessionId?: string, userId?: string | null): void {
    if (sessionId !== undefined) {
      this.sessionId = sessionId;
    }
    if (userId !== undefined) {
      this.userId = userId;
    }
  }

  /**
   * Get collector name
   */
  getName(): string {
    return this.name;
  }

  /**
   * Check if collector is initialized
   */
  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Log debug message
   */
  protected log(message: string, data?: any): void {
    if (this.debug) {
      console.log(`[${this.name}] ${message}`, data || '');
    }
  }

  /**
   * Log error
   */
  protected error(message: string, error?: any): void {
    console.error(`[${this.name}] ${message}`, error || '');
  }
}

